import React, { useState } from 'react';
import { Plus, Trash2, AlertTriangle } from 'lucide-react';

const AGList = ({ assemblees, userRole, buildings, onSelectAG, onNewAG, onDeleteAG, selectedBuilding, onBuildingChange }) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [agToDelete, setAgToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const getStatusColor = (status) => {
    const colors = {
      'Publiée': 'bg-blue-500',
      'Ouverte': 'bg-green-500',
      'En cours': 'bg-yellow-500',
      'Passée': 'bg-gray-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  const getBuildingDisplayName = (building) => {
    if (!building) return '';
    const nom = building.nom || '';
    const adresse = building.adresse || '';
    const codePostal = building.code_postal || '';
    const ville = building.ville || '';
    
    let display = nom;
    if (adresse) {
      display += display ? ` - ${adresse}` : adresse;
    }
    if (codePostal || ville) {
      display += `, ${codePostal} ${ville}`.trim();
    }
    return display || 'Immeuble inconnu';
  };

  const handleDeleteClick = (e, ag) => {
    e.stopPropagation(); // Prevent row click
    setAgToDelete(ag);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!agToDelete || !onDeleteAG) return;
    
    setIsDeleting(true);
    try {
      await onDeleteAG(agToDelete.id);
      setShowDeleteModal(false);
      setAgToDelete(null);
    } catch (error) {
      console.error('Error deleting AG:', error);
      alert('Erreur lors de la suppression de l\'assemblée');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setAgToDelete(null);
  };

  const filteredAssemblees = selectedBuilding 
    ? assemblees.filter(ag => ag.building_id === selectedBuilding)
    : assemblees;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h1 className="text-xl md:text-2xl font-bold text-gray-800">Assemblées Générales</h1>
        {userRole === 'gestionnaire' && (
          <button
            onClick={onNewAG}
            className="bg-gradient-to-r from-[#00D4AA] to-[#00D3A8] hover:opacity-90 text-white py-2 px-6 rounded-lg transition-all flex items-center justify-center space-x-2"
            data-testid="new-ag-btn"
            data-tour="new-ag-btn"
          >
            <Plus size={20} />
            <span>Nouvelle AG</span>
          </button>
        )}
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b border-gray-200">
          <select 
            className="px-3 py-2 border border-gray-300 rounded-lg w-full sm:w-auto"
            value={selectedBuilding || ''}
            onChange={(e) => onBuildingChange(e.target.value)}
          >
            <option value="">Tous les immeubles</option>
            {buildings?.map(b => (
              <option key={b.id} value={b.id}>{getBuildingDisplayName(b)}</option>
            ))}
          </select>
        </div>

        {/* Desktop Table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full" data-testid="ag-table">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Titre</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Adresse</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                {userRole === 'gestionnaire' && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredAssemblees.map((ag) => (
                <tr
                  key={ag.id}
                  onClick={() => onSelectAG(ag)}
                  className="hover:bg-gray-50 cursor-pointer"
                  data-testid={`ag-row-${ag.id}`}
                >
                  <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">{ag.titre}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                    {new Date(ag.date).toLocaleDateString('fr-FR')}
                  </td>
                  <td className="px-6 py-4 text-gray-500">{ag.lieu}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-3 py-1 rounded-full text-white text-sm ${getStatusColor(ag.status)}`}>
                      {ag.status}
                    </span>
                  </td>
                  {userRole === 'gestionnaire' && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button 
                        onClick={(e) => { e.stopPropagation(); onSelectAG(ag); }}
                        className="text-[#00D3A8] hover:text-[#00C7A1] mr-3"
                      >
                        Éditer
                      </button>
                      <button 
                        onClick={(e) => handleDeleteClick(e, ag)}
                        className="text-red-600 hover:text-red-800"
                        data-testid={`delete-ag-${ag.id}`}
                      >
                        Supprimer
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="md:hidden divide-y divide-gray-100">
          {filteredAssemblees.map((ag) => (
            <div
              key={ag.id}
              className="p-4 hover:bg-gray-50"
              data-testid={`ag-card-${ag.id}`}
            >
              <div 
                onClick={() => onSelectAG(ag)}
                className="cursor-pointer"
              >
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-medium text-gray-900">{ag.titre}</h3>
                  <span className={`px-2 py-0.5 rounded-full text-white text-xs ${getStatusColor(ag.status)}`}>
                    {ag.status}
                  </span>
                </div>
                <p className="text-sm text-gray-500">{ag.lieu}</p>
                <p className="text-sm text-gray-400 mt-1">
                  {new Date(ag.date).toLocaleDateString('fr-FR')}
                </p>
              </div>
              {userRole === 'gestionnaire' && (
                <div className="flex items-center justify-end mt-3 pt-3 border-t border-gray-100 space-x-3">
                  <button 
                    onClick={() => onSelectAG(ag)}
                    className="text-sm text-[#00D3A8] hover:text-[#00C7A1]"
                  >
                    Éditer
                  </button>
                  <button 
                    onClick={(e) => handleDeleteClick(e, ag)}
                    className="text-sm text-red-600 hover:text-red-800 flex items-center space-x-1"
                  >
                    <Trash2 size={14} />
                    <span>Supprimer</span>
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        {filteredAssemblees.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            Aucune assemblée générale
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && agToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div 
            className="bg-white rounded-xl shadow-xl max-w-md w-full p-6"
            data-testid="delete-ag-modal"
          >
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="text-red-600" size={24} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Confirmer la suppression</h3>
                <p className="text-sm text-gray-500">Cette action est irréversible</p>
              </div>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-700">
                Êtes-vous sûr de vouloir supprimer l'assemblée générale <strong>"{agToDelete.titre}"</strong> ?
              </p>
              <p className="text-xs text-gray-500 mt-2">
                Toutes les résolutions et votes associés seront également supprimés.
              </p>
            </div>
            
            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={handleCancelDelete}
                disabled={isDeleting}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                data-testid="cancel-delete-btn"
              >
                Annuler
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center space-x-2 disabled:opacity-50"
                data-testid="confirm-delete-btn"
              >
                {isDeleting ? (
                  <>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span>Suppression...</span>
                  </>
                ) : (
                  <>
                    <Trash2 size={16} />
                    <span>Supprimer</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AGList;
